import { useEffect, useRef, useCallback } from 'react';

const STORAGE_PREFIX = 'dt-v2-col-sizes:';

interface UseColumnSizePersistenceConfig {
  enabled: boolean;
  tableKey: string;
}

/**
 * Reads/writes column sizes to localStorage.
 *
 * On mount, loads the saved sizes. Provides a `save` callback that debounces
 * writes (300ms) to avoid thrashing during rapid resize drags.
 *
 * Reconciliation: fields that no longer exist in the schema are stripped.
 */
export function useColumnSizePersistence(config: UseColumnSizePersistenceConfig) {
  const { enabled, tableKey } = config;
  const storageKey = STORAGE_PREFIX + tableKey;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback((): Record<string, number> => {
    if (!enabled || !tableKey) return {};
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
      const result: Record<string, number> = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === 'number' && value > 0) {
          result[key] = value;
        }
      }
      return result;
    } catch {
      return {};
    }
  }, [enabled, tableKey, storageKey]);

  const save = useCallback(
    (sizes: Record<string, number>) => {
      if (!enabled || !tableKey) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        try {
          if (Object.keys(sizes).length === 0) {
            localStorage.removeItem(storageKey);
          } else {
            localStorage.setItem(storageKey, JSON.stringify(sizes));
          }
        } catch {
          // Storage full or unavailable — silently ignore
        }
      }, 300);
    },
    [enabled, tableKey, storageKey],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { load, save };
}

/**
 * Reconciles saved column sizes against the current schema columns.
 * Removes entries for fields that no longer exist in the schema.
 */
export function reconcileColumnSizes(
  savedSizes: Record<string, number>,
  schemaFields: string[],
): Record<string, number> {
  if (Object.keys(savedSizes).length === 0) return {};
  const schemaSet = new Set(schemaFields);
  const result: Record<string, number> = {};
  for (const [field, size] of Object.entries(savedSizes)) {
    if (schemaSet.has(field)) {
      result[field] = size;
    }
  }
  return result;
}
